import { useEffect, useRef } from "react";
import type { MapTool } from "../../../application/map/tools/types";
import { useMapTool } from "../context/MapToolContext";
import { useMapEdit } from "../context/MapEditContext";
import type { ToolDeps } from "./types";

import { makeEditVerticesTool } from "./useEditVerticesTool";
import { makeDrawBoundaryTool } from "./useDrawBoundaryTool";
import { makeDrawZoneTool } from "./useDrawZoneTool";
import { makeCutTool } from "./useCutTool";
import { makeEraseTool } from "./useEraseTool";
import { makeMergePolysTool } from "./useMergePolysTool";
import { makeMergeHolesTool } from "./useMergeHolesTool";
import { makeLassoTool } from "./useLassoTool";
import type { LassoToolConfig } from "./useLassoTool";
import { makePointAddTool } from "./usePointAddTool";
import { makePointDeleteTool } from "./usePointDeleteTool";
import { makePointModifyTool } from "./usePointModifyTool";
import { makeDeleteTool } from "./useDeleteTool";

const DEFAULT_LASSO_CONFIG: LassoToolConfig = {
	getLassoMetadata: () => [],
	onLassoComplete: undefined,
};

export function useToolRegistration(lassoConfig?: LassoToolConfig) {
	const { register, deactivate } = useMapTool();
	const editCtx = useMapEdit();

	// Store lassoConfig in a ref so it's always current without
	// causing tool re-creation (which wipes active tool state).
	const lassoConfigRef = useRef(lassoConfig);
	lassoConfigRef.current = lassoConfig;

	useEffect(() => {
		if (!editCtx.drawEngine) return;

		const deps: ToolDeps = {
			undoStack: editCtx.undoStack,
			drawEngine: editCtx.drawEngine,
			toolOverlay: editCtx.toolOverlay,
			getSelectedJobId: editCtx.getSelectedJobId,
			getSelectedPolygonId: editCtx.getSelectedPolygonId,
			getBoundaryFC: editCtx.getBoundaryFC,
			getZonesFC: editCtx.getZonesFC,
			getPointsFC: editCtx.getPointsFC,
			getCoresFC: editCtx.getCoresFC,
			applyBoundary: editCtx.applyBoundary,
			applyZones: editCtx.applyZones,
			applyPoints: editCtx.applyPoints,
			applyCores: editCtx.applyCores,
			showError: editCtx.showError,
			deactivateTool: deactivate,
		};

		const tools: MapTool[] = [
			makeEditVerticesTool(deps),
			makeDrawBoundaryTool(deps),
			makeDrawZoneTool(deps),
			makeCutTool(deps),
			makeEraseTool(deps),
			makeDeleteTool(deps),
			makeMergePolysTool(deps),
			makeMergeHolesTool(deps),
			makeLassoTool(deps, {
				get getLassoMetadata() {
					return (lassoConfigRef.current ?? DEFAULT_LASSO_CONFIG).getLassoMetadata;
				},
				get onLassoComplete() {
					return (lassoConfigRef.current ?? DEFAULT_LASSO_CONFIG).onLassoComplete;
				},
				get requestFeatureType() {
					return (lassoConfigRef.current ?? DEFAULT_LASSO_CONFIG).requestFeatureType;
				},
			}),
			makePointAddTool(deps),
			makePointDeleteTool(deps),
			makePointModifyTool(deps),
		];

		for (const tool of tools) {
			register(tool);
		}
	}, [editCtx.drawEngine, register, deactivate]);
}
